// AuthContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import type {
  AuthUser,
  AuthContextType,
  BaaStatus,
  PendingLegalDoc,
  PermissionsByCategory,
  PermissionString,
  PlanEntitlements,
} from "../types/permissions";
import { DEFAULT_AGENCY_TIMEZONE } from "../types/permissions";
import { apiClient } from "../services/api-client";
import { ApiError } from "../dtos/common/api.dto";
import { resolveFeatures } from "../constants/features";
import { normalizeSubscription } from "../utils/subscription";
import { normalizeSandbox } from "../utils/sandbox";
import type { MySubscriptionDto } from "../dtos/billing/subscription.dto";

/** Shape of GET /users/me/permissions (Phase 5 adds legal, BAA version and agency fields). */
interface MyPermissionsResponse {
  permissions: string[];
  permissionsByCategory: Record<string, string[]>;
  baaAccepted?: boolean;
  baaSignerRole?: string | null;
  baaStatus?: BaaStatus;
  baaGraceEndsAt?: string | null;
  currentBaaVersion?: string | null;
  pendingLegal?: PendingLegalDoc[];
  agency?: { timezone?: string | null; evvCollectPdnVisits?: boolean } | null;
  mfa?: { required: boolean; enrolled: boolean | null };
  features?: Record<string, boolean>;
  /** Phase 16A: plan entitlements (offlineMode is Core and above); older backends omit it. */
  entitlements?: Record<string, unknown>;
  /** Phase 13: subscription state; older backends omit it. */
  subscription?: MySubscriptionDto | null;
  /** Demo sandbox: seed progress, delete date and personas; null for a normal tenant. */
  sandbox?: unknown;
}

const BAA_STATUSES: ReadonlySet<string> = new Set(["accepted", "grace", "expired", "none"]);

/** Use baaStatus when valid; otherwise derive it from the legacy baaAccepted flag. */
export function resolveBaaStatus(status: unknown, baaAccepted: boolean | undefined): BaaStatus {
  if (typeof status === "string" && BAA_STATUSES.has(status)) return status as BaaStatus;
  return baaAccepted ? "accepted" : "none";
}

/** Keep boolean entitlements only; anything else (or an older backend) means not included. */
export function normalizeEntitlements(value: unknown): PlanEntitlements {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(([, v]) => typeof v === "boolean")
  ) as PlanEntitlements;
}

/** Keep only well-formed pending legal entries. */
function normalizePendingLegal(value: unknown): PendingLegalDoc[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (doc): doc is PendingLegalDoc =>
      !!doc &&
      typeof doc === "object" &&
      ((doc as PendingLegalDoc).docType === "tos" || (doc as PendingLegalDoc).docType === "privacy") &&
      typeof (doc as PendingLegalDoc).version === "string"
  );
}

// 1. Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 2. Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [accountSetupIncomplete, setAccountSetupIncomplete] = useState(false);

  /**
   * Load user's permissions from backend
   * Called after user authentication completes
   * This fetches the user's permissions and stores them in context
   */
  const loadPermissions = useCallback(async () => {
    if (!user?.id) {
      console.warn("Cannot load permissions: user not authenticated");
      return;
    }

    setIsLoading(true);
    setError(null);
    setAccountSetupIncomplete(false);

    try {
      // Call backend endpoint to get user permissions
      const response = await apiClient.get<MyPermissionsResponse>("/users/me/permissions");

      // Update user object with permissions
      setUser((prevUser) => {
        if (!prevUser) return null;

        // Cast permissions array items to PermissionString branded type
        const permissions = (response.data?.permissions || []) as PermissionString[];
        
        // Cast and provide defaults for permissionsByCategory
        const permissionsByCategory = (response.data?.permissionsByCategory as unknown as PermissionsByCategory) || {
          phi: [],
          pii: [],
          nonPii: [],
          admin: [],
          general: [],
        };

        return {
          ...prevUser,
          permissions,
          permissionsByCategory,
          baaAccepted: response.data?.baaAccepted,
          baaSignerRole: response.data?.baaSignerRole,
          baaStatus: resolveBaaStatus(response.data?.baaStatus, response.data?.baaAccepted),
          baaGraceEndsAt: response.data?.baaGraceEndsAt ?? null,
          currentBaaVersion: response.data?.currentBaaVersion ?? null,
          pendingLegal: normalizePendingLegal(response.data?.pendingLegal),
          agencyTimezone: response.data?.agency?.timezone || DEFAULT_AGENCY_TIMEZONE,
          evvCollectPdnVisits: response.data?.agency?.evvCollectPdnVisits === true,
          mfa: response.data?.mfa,
          features: resolveFeatures(response.data?.features),
          subscription: normalizeSubscription(response.data?.subscription),
          entitlements: normalizeEntitlements(response.data?.entitlements),
          sandbox: normalizeSandbox(response.data?.sandbox),
        };
      });

      console.log(
        `✓ Loaded ${response.data?.permissions?.length || 0} permissions for user ${user.id}`
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load permissions";

      console.error("⚠ Error loading permissions:", errorMessage);
      setError(errorMessage);
      if (err instanceof ApiError && err.domainCode === "ACCOUNT_SETUP_INCOMPLETE") {
        setAccountSetupIncomplete(true);
      }

      // DO NOT throw — app should remain functional even if permissions endpoint fails
      // Backend will enforce authorization on all API calls regardless
      // Frontend permissions are cosmetic only
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  /**
   * Clear permissions (typically on logout)
   */
  const clearPermissions = useCallback(() => {
    setUser((prevUser) => {
      if (!prevUser) return null;

      // Destructure and remove permission fields to return clean AuthUser
      const { permissions: _permissions, permissionsByCategory: _pbc, features: _features, subscription: _subscription, entitlements: _entitlements, sandbox: _sandbox, ...userWithoutPermissions } = prevUser;
      return userWithoutPermissions as AuthUser;
    });
    setError(null);
    setAccountSetupIncomplete(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        error,
        accountSetupIncomplete,
        loadPermissions,
        refreshPermissions: loadPermissions,
        agencyTimezone: user?.agencyTimezone || DEFAULT_AGENCY_TIMEZONE,
        clearPermissions,
        permissionsLoaded,
        setPermissionsLoaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// 3. useAuth hook for consuming the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuth must be used within an AuthProvider. Make sure you are wrapping your app with <AuthProvider>."
    );
  }
  return context;
};